import { useState, useEffect, useRef } from "react";
import { API_BASE, getComments, createComment, uploadFile } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { RichTextarea } from "./RichTextarea";
import { renderRichBody } from "../lib/richText";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { MessageSquare, Send, Paperclip, Loader2, X as XIcon, FileText, ShieldCheck } from "lucide-react";

const ROLE_BADGES = {
  super_admin: { label: "Internal Team", className: "bg-blue-100 text-blue-800" },
  manager: { label: "Manager", className: "bg-indigo-100 text-indigo-800" },
  rep: { label: "Sales Rep", className: "bg-emerald-100 text-emerald-800" },
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function CommentThread({ threadType, threadId, title = "Conversation", description }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const fetchComments = async () => {
    try {
      const { data } = await getComments(threadType, threadId);
      setComments(data || []);
    } catch (e) { /* swallow on first load */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (threadId) fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadType, threadId]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [comments.length]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(file);
      setFiles((prev) => [...prev, data]);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removeFile = (id) => setFiles((p) => p.filter((f) => f.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim() && files.length === 0) {
      toast.error("Add a message or attach a file");
      return;
    }
    setPosting(true);
    try {
      await createComment({
        thread_type: threadType,
        thread_id: threadId,
        body: body.trim(),
        file_ids: files.map((f) => f.id),
      });
      setBody("");
      setFiles([]);
      await fetchComments();
      toast.success("Comment posted - team has been notified by email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card className="border" data-testid="comment-thread">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" /> {title}
        </CardTitle>
        <CardDescription className="flex items-center gap-1.5 text-xs">
          <ShieldCheck className="h-3 w-3" /> {description || "Permanent record. All messages are visible to the rep and Ready Prospect team."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Thread */}
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <MessageSquare className="h-10 w-10 opacity-30 mb-2" />
              <p className="text-sm">No messages yet — start the conversation</p>
            </div>
          ) : (
            comments.map((c) => {
              const badge = ROLE_BADGES[c.author_role] || { label: c.author_role, className: "bg-zinc-100 text-zinc-700" };
              return (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                    {(c.author_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{c.author_name || "Unknown"}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.className}`}>{badge.label}</span>
                      <span className="text-xs text-muted-foreground">{formatTime(c.created_at)}</span>
                    </div>
                    {c.body && <p className="text-sm mt-0.5 whitespace-pre-wrap leading-relaxed">{renderRichBody(c.body)}</p>}
                    {c.file_ids?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {c.file_ids.map((fid) => (
                          <a
                            key={fid}
                            href={`${API_BASE}/api/files/${fid}/download`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline bg-primary/5 px-2 py-1 rounded"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileText className="h-3 w-3" /> Attachment
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <form onSubmit={handleSubmit} className="border-t pt-3 space-y-2">
          <RichTextarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message... (visible to rep and Ready Prospect team)"
            rows={2}
            className="resize-none"
            data-testid="comment-body"
          />
          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-1.5 px-2 rounded bg-zinc-50 border text-xs">
                  <span className="flex items-center gap-1.5 truncate"><FileText className="h-3 w-3 shrink-0" />{f.filename}</span>
                  <button type="button" onClick={() => removeFile(f.id)} className="text-muted-foreground hover:text-foreground"><XIcon className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="comment-attach-btn">
              {uploading ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Uploading...</> : <><Paperclip className="mr-1 h-3 w-3" />Attach file</>}
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            <Button type="submit" disabled={posting || (!body.trim() && files.length === 0)} data-testid="post-comment-btn">
              {posting ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Posting...</> : <><Send className="mr-1.5 h-3.5 w-3.5" />Post</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
